import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import BuildIcon from '@material-ui/icons/Build';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import PeopleIcon from '@material-ui/icons/People';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import { QuickStatsSkeleton } from './SkeletonComponents';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
  },
  statIcon: {
    marginRight: theme.spacing(1.5),
    padding: theme.spacing(0.75),
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontWeight: 'bold',
    fontSize: '1.25rem',
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  // Icon colors
  incidenceIcon: {
    backgroundColor: '#ff7043',
  },
  assistanceIcon: {
    backgroundColor: '#9ccc65',
  },
  orderIcon: {
    backgroundColor: '#42a5f5',
  },
  customerIcon: {
    backgroundColor: '#2196f3',
  },
}));

const QuickStats = ({ data }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!data) return <QuickStatsSkeleton />;

  const stats = [
    {
      key: 'incidences',
      label: t('tables.incidence.name'),
      value: data.incidences || 0,
      icon: BuildIcon,
      iconClass: classes.incidenceIcon,
    },
    {
      key: 'assistance',
      label: t('tables.assistance.name'),
      value: data.assistance || 0,
      icon: AssignmentIcon,
      iconClass: classes.assistanceIcon,
    },
    {
      key: 'orders',
      label: t('tables.orders.name'),
      value: data.orders || 0,
      icon: ShoppingCartIcon,
      iconClass: classes.orderIcon,
    },
    {
      key: 'customers',
      label: t('tables.customers.name'),
      value: data.customers || 0,
      icon: PeopleIcon,
      iconClass: classes.customerIcon,
    },
  ];

  const total = stats.reduce((sum, stat) => sum + stat.value, 0);

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        <TrendingUpIcon color="primary" />
        {t('dashboard.this_week')}
      </Typography>
      
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          {t('dashboard.this_week_summary', { total })}
        </Typography>
      </Box>

      <Grid container spacing={1}>
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={12} key={stat.key}>
              <Box className={classes.statItem}>
                <Box className={`${classes.statIcon} ${stat.iconClass}`}>
                  <Icon fontSize="small" />
                </Box>
                <Box className={classes.statContent}>
                  <Typography className={classes.statValue}>
                    {stat.value}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default QuickStats;
